// Extracts an initial cabinet interior layout from the actual cabinet GLB.
//
// The parser only treats semantic section nodes as layout sections:
//   Shelf###      -> shelf section
//   Drawer / Drawer### / Drawer_Base### -> drawer section
//   Door Left### / Door_Left###   -> left half of a box door-pair section
//   Door Right### / Door_Right### -> right half of a box door-pair section
//
// Child meshes inherit the nearest semantic parent type, so meshes like
// Drawer_box, Drawer_Cap, Door_Left_Cap, or future handle-position meshes are
// included in their parent section bounds when they are nested correctly.
// Standalone helper/marker meshes are intentionally ignored; they should not
// create extra UI sections.

import { hasInvisibleObjectName } from '../BaseModelLoader';
import { ASYNC_SCRIPTS } from '../../../../../../../../../loadingLogic';
import { loadScriptPromise } from '../../../../../../../../../misc/loadScript';
import {
  CABINET_INTERIOR_SECTION_TYPE as SECTION_TYPE
} from '../../../../../../../constants/cabinetInteriorConstants';

const RAW_TYPE_TO_SECTION_TYPE = {
  shelf: SECTION_TYPE.shelf,
  drawer: SECTION_TYPE.drawer,
  door: SECTION_TYPE.box,
};

function classifyObjectName(name) {
  if (/^Shelf\d*$/.test(name)) {
    return 'shelf';
  }

  // Some cabinet GLBs use an unnumbered Drawer mesh, while others put the
  // complete drawer assembly under Drawer_Base, Drawer_Base001, etc.
  if (/^Drawer\d*$/.test(name) || /^Drawer_Base(?:$|[\d_.])/.test(name)) {
    return 'drawer';
  }

  if (/^Door[ _]Left(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_left';
  }

  if (/^Door[ _]Right(?:\d*|\.\d+)?$/.test(name)) {
    return 'door_right';
  }

  return null;
}

let threeRuntimePromise = null;

async function ensureThreeRuntime() {
  if (window.GLTFLoader && window.THREE) {
    return;
  }

  if (!threeRuntimePromise) {
    threeRuntimePromise = loadScriptPromise(ASYNC_SCRIPTS.three, 'three');
  }

  try {
    await threeRuntimePromise;
  } catch (error) {
    threeRuntimePromise = null;
    throw error;
  }

  if (!window.GLTFLoader || !window.THREE) {
    threeRuntimePromise = null;
    throw new Error('[parseCabinetSections] Three.js runtime did not initialize');
  }
}

async function loadGlbScene(url) {
  await ensureThreeRuntime();

  return new Promise((resolve, reject) => {
    const loader = new window.GLTFLoader();
    loader.load(url, (gltf) => resolve(gltf.scene), undefined, reject);
  });
}

function classifySectionObject(object) {
  let current = object;

  while (current && current.isObject3D) {
    const rawType = classifyObjectName(current.name || '');
    if (rawType) {
      return rawType;
    }

    current = current.parent;
  }

  return null;
}

function collectSectionMeshes(scene) {
  const THREE = window.THREE;
  const meshes = [];

  scene.traverse((child) => {
    if (!child.isMesh || hasInvisibleObjectName(child, scene)) {
      return;
    }

    const rawType = classifySectionObject(child);
    if (!rawType) {
      return;
    }

    const box = new THREE.Box3().setFromObject(child);
    meshes.push({
      name: child.name,
      rawType,
      centerY: (box.min.y + box.max.y) / 2,
      minY: box.min.y,
      maxY: box.max.y,
    });
  });

  return meshes;
}

/**
 * Door_Left / Door_Right names mark models authored for editable interiors.
 * Older models can contain partial Drawer/Shelf matches that must not be parsed.
 **/
function hasEditableInteriorFormat(meshes) {
  return meshes.some((mesh) => mesh.rawType === 'door_left' || mesh.rawType === 'door_right');
}

function getMeshHeight(mesh) {
  return mesh.maxY - mesh.minY;
}

function hasStrongVerticalOverlap(first, second) {
  const overlap = getVerticalOverlap(first, second);
  const minHeight = Math.min(getMeshHeight(first), getMeshHeight(second));

  return minHeight > 0 && overlap / minHeight > 0.5;
}

function mergeSectionGroup(group) {
  return {
    rawType: group[0].rawType,
    centerY: group.reduce((sum, mesh) => sum + mesh.centerY, 0) / group.length,
    minY: Math.min(...group.map((mesh) => mesh.minY)),
    maxY: Math.max(...group.map((mesh) => mesh.maxY)),
  };
}

function mergeOverlappingSectionMeshes(meshes) {
  const groups = [];

  meshes.forEach((mesh) => {
    const existingGroup = groups.find((group) => (
      group[0].rawType === mesh.rawType
      && group.some((groupMesh) => hasStrongVerticalOverlap(groupMesh, mesh))
    ));

    if (existingGroup) {
      existingGroup.push(mesh);
      return;
    }

    groups.push([mesh]);
  });

  return groups.map(mergeSectionGroup);
}

function mergeDoorPairs(meshes) {
  const doorLefts = meshes.filter((mesh) => mesh.rawType === 'door_left');
  const doorRights = meshes.filter((mesh) => mesh.rawType === 'door_right');
  const others = meshes.filter((mesh) => mesh.rawType !== 'door_left' && mesh.rawType !== 'door_right');

  const usedRights = new Set();
  const doors = [];

  doorLefts.forEach((left) => {
    let best = null;
    let bestDist = Infinity;

    doorRights.forEach((right) => {
      if (usedRights.has(right.name)) {
        return;
      }

      const dist = Math.abs(left.centerY - right.centerY);
      if (dist < bestDist) {
        bestDist = dist;
        best = right;
      }
    });

    if (best) {
      usedRights.add(best.name);
      doors.push({
        rawType: 'door',
        centerY: (left.centerY + best.centerY) / 2,
        minY: Math.min(left.minY, best.minY),
        maxY: Math.max(left.maxY, best.maxY),
      });
      return;
    }

    doors.push({ ...left, rawType: 'door' });
  });

  doorRights.forEach((right) => {
    if (!usedRights.has(right.name)) {
      doors.push({ ...right, rawType: 'door' });
    }
  });

  return mergeOverlappingSectionMeshes([...others, ...doors]);
}

function getSectionBounds(meshes) {
  if (meshes.length === 0) {
    return null;
  }

  return {
    minY: Math.min(...meshes.map((mesh) => mesh.minY)),
    maxY: Math.max(...meshes.map((mesh) => mesh.maxY)),
  };
}

function getVerticalOverlap(first, second) {
  return Math.min(first.maxY, second.maxY) - Math.max(first.minY, second.minY);
}

function isShelfInsideBox(shelf, box) {
  if (shelf.centerY < box.minY || shelf.centerY > box.maxY) {
    return false;
  }

  return getVerticalOverlap(shelf, box) > 0;
}

function mergeInteriorShelvesIntoBoxes(meshes) {
  const boxes = meshes
    .filter((mesh) => mesh.rawType === 'door')
    .map((box) => ({ ...box, shelfCount: 0 }));
  const remainingMeshes = meshes.filter((mesh) => mesh.rawType !== 'door' && mesh.rawType !== 'shelf');

  meshes
    .filter((mesh) => mesh.rawType === 'shelf')
    .forEach((shelf) => {
      const parentBox = boxes
        .filter((box) => isShelfInsideBox(shelf, box))
        .sort((a, b) => getVerticalOverlap(shelf, b) - getVerticalOverlap(shelf, a))[0];

      if (parentBox) {
        parentBox.shelfCount += 1;
        return;
      }

      remainingMeshes.push(shelf);
    });

  return [...remainingMeshes, ...boxes];
}

function buildSectionsWithRatios(mergedMeshes, totalMinY, totalMaxY) {
  const totalHeight = totalMaxY - totalMinY;
  if (totalHeight <= 0 || mergedMeshes.length === 0) {
    return [];
  }

  const sectionMeshes = mergeInteriorShelvesIntoBoxes(mergedMeshes);
  const sorted = [...sectionMeshes].sort((a, b) => b.centerY - a.centerY);

  return sorted.map((mesh, index) => {
    let top = mesh.maxY;
    let bottom = mesh.minY;

    if (mesh.rawType === 'shelf') {
      const previousMesh = sorted[index - 1];
      const nextMesh = sorted[index + 1];

      top = previousMesh ? previousMesh.minY : top;
      bottom = nextMesh ? nextMesh.maxY : bottom;
    }

    const isShelfSection = mesh.rawType === 'door' && mesh.shelfCount > 0;

    return {
      type: isShelfSection
        ? RAW_TYPE_TO_SECTION_TYPE.shelf
        : RAW_TYPE_TO_SECTION_TYPE[mesh.rawType] || SECTION_TYPE.box,
      heightRatio: Math.max(0, top - bottom) / totalHeight,
      quantity: mesh.rawType === 'door' ? mesh.shelfCount : undefined,
      hasDoor: mesh.rawType === 'door',
    };
  });
}

function disposeScene(scene) {
  scene.traverse((child) => {
    if (!child.isMesh) {
      return;
    }

    child.geometry.dispose();
    if (Array.isArray(child.material)) {
      child.material.forEach((material) => material.dispose());
    } else if (child.material) {
      child.material.dispose();
    }
  });
}

const cacheByUrl = new Map();

/**
 * Parses editable section ratios from an already-loaded cabinet instance.
 * This avoids a second model request when the caller already owns the GLB scene.
 */
export function getCabinetSectionsFromScene(scene) {
  if (!scene) {
    return null;
  }

  const raw = collectSectionMeshes(scene);
  if (!hasEditableInteriorFormat(raw)) {
    return null;
  }

  const merged = mergeDoorPairs(raw);
  const sectionBounds = getSectionBounds(merged);
  if (!sectionBounds) {
    return null;
  }

  const sections = buildSectionsWithRatios(merged, sectionBounds.minY, sectionBounds.maxY);
  return sections.length ? sections : null;
}

/**
 * Parses section ratios from a cabinet model URL.
 * Returns null if model loading or mesh-name parsing fails.
 */
export async function getCabinetSectionsFromModel(url) {
  if (!url) {
    return null;
  }

  if (cacheByUrl.has(url)) {
    return cacheByUrl.get(url);
  }

  try {
    const scene = await loadGlbScene(url);
    const sections = getCabinetSectionsFromScene(scene);
    disposeScene(scene);

    cacheByUrl.set(url, sections);
    return sections;
  } catch (error) {
    console.warn('[cabinetSectionsParser] Could not parse cabinet model sections:', error.message);
    return null;
  }
}
